/**
 * Client-side helpers for email template preview and test-send drafts.
 *
 * The main Worker is still the authority for delivery; this module mirrors the
 * managed template renderer closely enough to preview drafts before save.
 *
 * @copyright 2026 Glimbr Pty Ltd
 * @version 2026-04-25-v1
 */
import type { AdminSession } from './auth';
import type { TemplateDetail } from './types';

const HANDLEBARS_PATTERN = /{{{?\s*([^{}]+?)\s*}}}?/g;

const SAMPLE_VALUES: Record<string, string> = {
    access_level: 'Editor',
    alert_date: '25 Apr 2026',
    alert_type: 'New sign-in',
    amount: '$29.00',
    approval_url: 'https://producerdashboard.app/share/review-demo',
    billing_cycle: 'monthly',
    billing_url: 'https://producerdashboard.app/settings/billing',
    cancel_hours: '24',
    change_date: '25 Apr 2026',
    collaborator_name: 'Taylor Morgan',
    current_plan: 'Studio',
    dashboard_url: 'https://producerdashboard.app/dashboard',
    data_retention_days: '30',
    days_active: '14',
    days_remaining: '3',
    deletion_date: '25 May 2026',
    device: 'Chrome on macOS',
    download_url: 'https://producerdashboard.app/exports/demo',
    due_date: '30 Apr 2026',
    end_date: '30 Apr 2026',
    expires_at: '30 Apr 2026',
    expires_minutes: '15',
    expiry_date: '25 May 2026',
    expiry_hours: '24',
    expiry_minutes: '15',
    export_data_url: 'https://producerdashboard.app/exports/account',
    export_type: 'Account data',
    feedback_url: 'https://producerdashboard.app/feedback',
    folder_path: '/PRODUCER-DASHBOARD/Demo Track',
    invoice_url: 'https://producerdashboard.app/invoices/demo',
    ip_address: '203.0.113.42',
    master_split: '50%',
    message_from_owner: 'Please review the latest bounce when you have a minute.',
    new_email: 'new-address@example.com',
    new_plan: 'Studio',
    new_price: '$29.00',
    old_plan: 'Pro',
    owner_name: 'Ari Producer',
    payment_date: '25 Apr 2026',
    plan_name: 'Studio',
    playlist_title: 'Client Bounce Review',
    price: '$29.00',
    prorated_amount: '$12.50',
    publishing_split: '50%',
    purpose: 'account verification',
    reactivate_url: 'https://producerdashboard.app/settings/billing',
    recipient_name: 'Jamie Client',
    removal_date: '25 Apr 2026',
    renewal_date: '25 May 2026',
    renew_url: 'https://producerdashboard.app/settings/billing',
    requires_password: 'No',
    reset_link: 'https://producerdashboard.app/reset-password?token=sample',
    reset_password_url: 'https://producerdashboard.app/reset-password',
    retry_date: '26 Apr 2026',
    role: 'Producer',
    signup_url: 'https://producerdashboard.app/signup',
    song_name: 'Midnight Demo',
    support_email: 'hello@glimbr.ai',
    track_count: '42',
    track_limit: '100',
    track_summary: '3 bounces ready for review',
    trial_end_date: '9 May 2026',
    unsubscribe_url: 'https://producerdashboard.app/unsubscribe?email=sample%40example.com',
    update_payment_url: 'https://producerdashboard.app/settings/billing',
    upgrade_url: 'https://producerdashboard.app/settings/billing',
    usage_percent: '72%',
    verification_link: 'https://producerdashboard.app/verify?token=sample'
};

export interface TemplatePreviewContent {
    subject: string;
    htmlContent: string;
    textContent: string;
}

export interface TemplatePreviewResult {
    subject: string;
    html: string;
    text: string;
    placeholderData: Record<string, string>;
}

export interface TemplateTestEmailPayload {
    version_id: string;
    subject: string;
    html_content: string;
    text_content: string;
    from_email: string;
    from_name: string;
    reply_to_email: string | null;
    reply_to_name: string | null;
    supported_placeholders: string[];
}

function uniqueSorted(values: string[]): string[] {
    return Array.from(new Set(values.filter(Boolean))).sort((left, right) =>
        left.localeCompare(right)
    );
}

function nonEmpty(value: string | null | undefined): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function titleFromPlaceholder(placeholder: string): string {
    return placeholder
        .split(/[_-]+/)
        .filter(Boolean)
        .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
        .join(' ');
}

function nameFromEmail(email: string | null): string | null {
    if (!email) return null;
    const localPart = email.split('@')[0];
    if (!localPart) return null;
    return titleFromPlaceholder(localPart);
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function sessionValueForPlaceholder(
    placeholder: string,
    session: AdminSession | null
): string | null {
    const email = nonEmpty(session?.email) ?? null;
    const name = nonEmpty(session?.name) ?? nameFromEmail(email);

    if (['name', 'user_name'].includes(placeholder)) return name;
    if (['email', 'user_email'].includes(placeholder)) return email;
    if (placeholder === 'admin_id') return nonEmpty(session?.admin_id);
    if (placeholder === 'admin_role') return nonEmpty(session?.role);

    return null;
}

function sampleValueForPlaceholder(placeholder: string): string {
    return SAMPLE_VALUES[placeholder] ?? (titleFromPlaceholder(placeholder) || 'Sample value');
}

function renderContent(content: string, data: Record<string, string>, mode: 'html' | 'text') {
    return content.replace(HANDLEBARS_PATTERN, (fullMatch, innerToken) => {
        const token = String(innerToken || '').trim();
        if (!token || token === 'else') return '';
        if (['#', '/', '!', '>'].includes(token[0] || '')) return '';

        const parts = token.split(/\s+/).filter(Boolean);
        if (parts.length !== 1) return '';

        const value = data[parts[0]] ?? '';
        if (mode === 'html' && !fullMatch.startsWith('{{{')) {
            return escapeHtml(value);
        }
        return value;
    });
}

export function extractTemplatePlaceholders(content: TemplatePreviewContent): string[] {
    const placeholders: string[] = [];

    for (const value of [content.subject, content.htmlContent, content.textContent]) {
        for (const match of value.matchAll(HANDLEBARS_PATTERN)) {
            const token = String(match[1] || '').trim();
            if (!token || token === 'else') continue;
            if (['#', '/', '!', '>'].includes(token[0] || '')) continue;

            const parts = token.split(/\s+/).filter(Boolean);
            if (parts.length === 1) {
                placeholders.push(parts[0]);
            }
        }
    }

    return uniqueSorted(placeholders);
}

export function buildTemplatePreviewData(
    placeholders: string[],
    session: AdminSession | null
): Record<string, string> {
    const data: Record<string, string> = {};
    for (const placeholder of uniqueSorted(placeholders)) {
        data[placeholder] =
            sessionValueForPlaceholder(placeholder, session) ??
            sampleValueForPlaceholder(placeholder);
    }
    return data;
}

export function renderTemplatePreview(
    content: TemplatePreviewContent,
    placeholders: string[],
    session: AdminSession | null
): TemplatePreviewResult {
    const detected = extractTemplatePlaceholders(content);
    const placeholderData = buildTemplatePreviewData([...placeholders, ...detected], session);

    return {
        subject: renderContent(content.subject, placeholderData, 'text'),
        html: renderContent(content.htmlContent, placeholderData, 'html'),
        text: renderContent(content.textContent, placeholderData, 'text'),
        placeholderData
    };
}

export function canPreviewTemplateDraft(content: TemplatePreviewContent): boolean {
    return Boolean(
        content.subject.trim() && (content.htmlContent.trim() || content.textContent.trim())
    );
}

export function buildTemplateTestEmailPayload({
    detail,
    selectedVersionId,
    subject,
    htmlContent,
    textContent
}: {
    detail: TemplateDetail;
    selectedVersionId: string;
    subject: string;
    htmlContent: string;
    textContent: string;
}): TemplateTestEmailPayload {
    return {
        version_id: selectedVersionId,
        subject,
        html_content: htmlContent,
        text_content: textContent,
        from_email: detail.from_email,
        from_name: detail.from_name,
        reply_to_email: detail.reply_to_email,
        reply_to_name: detail.reply_to_name,
        supported_placeholders: detail.supported_placeholders
    };
}
